
import { useState, useRef, useEffect, useMemo } from 'react';
import OptionBar from './OptionBar';
import RecommendationCapsule from './RecommendationCapsule';
import ProgressPanel from './ProgressPanel';
import ResultCard from './ResultCard';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  result?: {
    data: any[];
    sql: string;
    chartType: 'bar' | 'line' | 'pie';
  };
  hasAmbiguity?: boolean;
  ambiguityReason?: string;
  hasPermissionError?: boolean;
  permissionErrorReason?: string;
}

interface AiDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// 关键词匹配的问题库
const KEYWORD_QUESTIONS: Record<string, string[]> = {
  '客流': [
    '请查询昨天累积客流数',
    '本周客流量趋势分析',
    '实时客流数据统计',
    '客流高峰时段分布',
    '各景区客流量对比'
  ],
  '门票': [
    '最近一周的门票销售趋势',
    '门票销售额统计',
    '不同类型门票销量对比',
    '门票退款率分析',
    '线上线下门票销售对比'
  ],
  '游客': [
    '游客年龄分布情况',
    '游客来源地域统计',
    '游客满意度调查结果',
    '游客停留时长分析',
    '回头客比例统计'
  ],
  '销售': [
    '本月销售额统计',
    '销售渠道转化率对比',
    '销售趋势预测',
    '热门产品销售排行',
    '销售目标完成情况'
  ],
  '景区': [
    '各景区热度排名',
    '景区游览路线分析',
    '景区设施使用率统计',
    '景区评价分析',
    '景区容量预警'
  ],
  '预测': [
    '预测下个月的客流量峰值',
    '预测节假日门票销量',
    '预测热门时段',
    '预测游客增长趋势',
    '预测收入变化'
  ],
  '对比': [
    '对比不同渠道的转化率',
    '对比各景区客流量',
    '对比不同时段的销售额',
    '对比线上线下数据',
    '对比同期历史数据'
  ],
  '分析': [
    '分析游客的年龄分布情况',
    '分析用户行为路径',
    '分析热门时段的客流量',
    '分析销售数据趋势',
    '分析用户满意度'
  ],
  '统计': [
    '统计热门时段的客流量',
    '统计各渠道销售数据',
    '统计用户来源分布',
    '统计产品销量排行',
    '统计服务评价'
  ],
  '趋势': [
    '最近一周的销售趋势',
    '客流量变化趋势',
    '用户增长趋势',
    '收入趋势分析',
    '满意度趋势'
  ]
};

// 需要特殊权限的关键词
const PERMISSION_REQUIRED_KEYWORDS = ['预测', '预估', '预判', '趋势预测', '未来'];

export default function AiDialog({ isOpen, onClose }: AiDialogProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    '票务系统',
    '文旅客情数据',
    '腾讯 We 分析数据',
    '天威客流数据'
  ]);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [currentThinkingTimer, setCurrentThinkingTimer] = useState<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 根据输入内容匹配相关问题
  const matchedQuestions = useMemo(() => {
    if (!inputValue.trim()) return [];
    
    const matches: string[] = [];
    const input = inputValue.toLowerCase();
    
    // 遍历关键词库，查找匹配的问题
    Object.keys(KEYWORD_QUESTIONS).forEach(keyword => {
      if (input.includes(keyword)) {
        matches.push(...KEYWORD_QUESTIONS[keyword]);
      }
    });
    
    // 去重并限制数量
    return [...new Set(matches)].slice(0, 5);
  }, [inputValue]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isOpen) return null;

  const handleClearChat = () => {
    setMessages([]);
    setShowClearConfirm(false);
  };

  const handleEditMessage = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditingContent(content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const handleStopThinking = () => {
    if (currentThinkingTimer) {
      clearTimeout(currentThinkingTimer);
      setCurrentThinkingTimer(null);
    }
    setIsThinking(false);
    
    // 添加中断消息
    const interruptMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: '查询已被用户中断。您可以重新提问或尝试其他问题。',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, interruptMessage]);
  };

  const handleSaveEdit = (messageId: string) => {
    if (!editingContent.trim() || isThinking) return;

    // 找到要编辑的消息的索引
    const messageIndex = messages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1) return;

    // 删除该消息及其之后的所有消息
    const newMessages = messages.slice(0, messageIndex);
    
    // 添加编辑后的用户消息
    const editedMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: editingContent,
      timestamp: new Date(),
    };

    setMessages([...newMessages, editedMessage]);
    setEditingMessageId(null);
    setEditingContent('');
    setIsThinking(true);

    // 模拟 AI 响应
    const timer = setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '根据您更新的问题，我已经为您重新分析了相关数据。',
        timestamp: new Date(),
        result: {
          data: [
            { name: '周一', value: 1200 },
            { name: '周二', value: 1800 },
            { name: '周三', value: 1500 },
            { name: '周四', value: 2100 },
            { name: '周五', value: 2800 },
            { name: '周六', value: 3500 },
            { name: '周日', value: 3200 },
          ],
          sql: 'SELECT date, COUNT(*) as value FROM tickets WHERE date >= DATE_SUB(NOW(), INTERVAL 7 DAY) GROUP BY date ORDER BY date;',
          chartType: 'bar',
        },
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsThinking(false);
      setCurrentThinkingTimer(null);
    }, 2000);
    
    setCurrentThinkingTimer(timer);
  };

  const handleSend = () => {
    if (!inputValue.trim() || isThinking) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsThinking(true);

    // 模拟 AI 响应 - 检测权限和歧义
    const timer = setTimeout(() => {
      // 检测是否需要特殊权限
      const needsPermission = PERMISSION_REQUIRED_KEYWORDS.some(keyword => 
        userMessage.content.includes(keyword)
      );
      
      // 检测问题是否存在歧义
      const hasAmbiguity = !needsPermission && (
        userMessage.content.includes('门票销售趋势') || 
        userMessage.content.includes('销售情况') ||
        userMessage.content.includes('数据分析')
      );
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: needsPermission
          ? '抱歉，您当前没有权限执行此查询操作。'
          : hasAmbiguity 
            ? '您的问题描述不够具体，我无法准确理解您的需求。请提供更详细的信息，以便我为您生成准确的查询结果。'
            : '根据您选择的数据源,我已经为您分析了相关数据。',
        timestamp: new Date(),
        hasPermissionError: needsPermission,
        permissionErrorReason: needsPermission
          ? '该查询需要"高级数据分析"权限才能执行。请联系系统管理员申请相应权限。'
          : undefined,
        hasAmbiguity: hasAmbiguity,
        ambiguityReason: hasAmbiguity 
          ? '为了提供更准确的分析结果，建议您在问题中明确以下信息：\n\n• 具体的时间范围（如：最近一周、本月、昨天等）\n• 需要查询的具体指标（如：数量、金额、比例等）\n• 是否需要分类统计（如：按类型、按渠道、按地区等）\n• 其他相关的筛选条件'
          : undefined,
        result: (needsPermission || hasAmbiguity) ? undefined : {
          data: [
            { name: '周一', value: 1200 },
            { name: '周二', value: 1800 },
            { name: '周三', value: 1500 },
            { name: '周四', value: 2100 },
            { name: '周五', value: 2800 },
            { name: '周六', value: 3500 },
            { name: '周日', value: 3200 },
          ],
          sql: 'SELECT date, COUNT(*) as value FROM tickets WHERE date >= DATE_SUB(NOW(), INTERVAL 7 DAY) GROUP BY date ORDER BY date;',
          chartType: 'bar',
        },
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsThinking(false);
      setCurrentThinkingTimer(null);
    }, 2000);
    
    setCurrentThinkingTimer(timer);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-bold">AI</span>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">AI 智能问数</h2>
                <p className="text-xs text-gray-500">已选择 {selectedOptions.length} 个数据源</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button 
                  onClick={() => setShowClearConfirm(true)}
                  className="px-3 py-1.5 flex items-center gap-1.5 text-xs text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                  <span>清空记录</span>
                </button>
              )}
              <button 
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-lg"></i>
              </button>
            </div>
          </div>

          {/* Option Bar */}
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
            <OptionBar 
              selectedOptions={selectedOptions}
              onOptionsChange={setSelectedOptions}
            />
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-chat-smile-3-line text-blue-600 text-3xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">开始您的数据探索之旅</h3>
                  <p className="text-xs text-gray-500">选择数据范围，输入您的业务问题，AI 为您即时提供精准数据</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id}>
                    {message.type === 'user' ? (
                      <div className="flex justify-end group">
                        {editingMessageId === message.id ? (
                          <div className="flex-1 max-w-2xl">
                            <div className="bg-white border border-blue-300 rounded-xl p-2">
                              <textarea
                                value={editingContent}
                                onChange={(e) => setEditingContent(e.target.value)}
                                className="w-full px-2 py-1 bg-transparent outline-none resize-none text-xs"
                                rows={3}
                                autoFocus
                              />
                              <div className="flex items-center justify-end gap-1.5 mt-1.5">
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-2 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                                >
                                  取消
                                </button>
                                <button
                                  onClick={() => handleSaveEdit(message.id)}
                                  disabled={!editingContent.trim()}
                                  className="px-2 py-1 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                                >
                                  <i className="ri-send-plane-fill mr-0.5 text-xs"></i>
                                  重新提交
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditMessage(message.id, message.content)}
                              className="mr-2 w-6 h-6 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                              title="编辑问题"
                            >
                              <i className="ri-edit-line text-gray-600 text-xs"></i>
                            </button>
                            <div className="bg-blue-500 text-white px-3 py-2 rounded-2xl rounded-tr-sm max-w-2xl">
                              <p className="text-xs leading-relaxed">{message.content}</p>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex justify-start">
                          <div className="bg-gray-100 text-gray-900 px-3 py-2 rounded-2xl rounded-tl-sm max-w-2xl">
                            <p className="text-xs leading-relaxed">{message.content}</p>
                          </div>
                        </div>
                        
                        {/* 权限错误提示卡片 */}
                        {message.hasPermissionError && message.permissionErrorReason && (
                          <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
                            {/* Header */}
                            <div className="px-3 py-2 bg-red-100/50 border-b border-red-200 flex items-center gap-2">
                              <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center">
                                <i className="ri-lock-line text-white text-sm"></i>
                              </div>
                              <span className="text-xs font-semibold text-red-900">权限不足</span>
                            </div>
                            
                            {/* Content */}
                            <div className="px-3 py-3">
                              <p className="text-xs text-red-900 leading-relaxed whitespace-pre-line mb-3">
                                {message.permissionErrorReason}
                              </p>
                              
                              {/* 操作提示 */}
                              <div className="bg-white rounded-lg p-2.5 border border-red-200">
                                <div className="flex items-start gap-2">
                                  <i className="ri-user-settings-line text-red-600 text-sm mt-0.5"></i>
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-gray-900 mb-1.5">如何获取权限：</p>
                                    <ul className="text-xs text-gray-700 leading-relaxed space-y-1">
                                      <li className="flex items-start gap-1.5">
                                        <span className="text-red-500 mt-0.5">•</span>
                                        <span>联系您的系统管理员或负责人</span>
                                      </li>
                                      <li className="flex items-start gap-1.5">
                                        <span className="text-red-500 mt-0.5">•</span>
                                        <span>说明您需要使用的具体功能</span>
                                      </li>
                                      <li className="flex items-start gap-1.5">
                                        <span className="text-red-500 mt-0.5">•</span>
                                        <span>等待管理员审核并授予相应权限</span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              
                              {/* 提示文字 */}
                              <div className="mt-2 flex items-start gap-1.5">
                                <i className="ri-information-line text-red-600 text-xs mt-0.5"></i>
                                <p className="text-xs text-red-800">
                                  您可以尝试输入其他想了解的问题
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* 歧义提示卡片 */}
                        {message.hasAmbiguity && message.ambiguityReason && (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
                            {/* Header */}
                            <div className="px-3 py-2 bg-amber-100/50 border-b border-amber-200 flex items-center gap-2">
                              <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center">
                                <i className="ri-error-warning-line text-white text-sm"></i>
                              </div>
                              <span className="text-xs font-semibold text-amber-900">问题描述不够具体</span>
                            </div>
                            
                            {/* Content */}
                            <div className="px-3 py-3">
                              <p className="text-xs text-amber-900 leading-relaxed whitespace-pre-line mb-3">
                                {message.ambiguityReason}
                              </p>
                              
                              {/* 操作提示 */}
                              <div className="bg-white rounded-lg p-2.5 border border-amber-200">
                                <div className="flex items-start gap-2">
                                  <i className="ri-lightbulb-flash-line text-amber-600 text-sm mt-0.5"></i>
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-gray-900 mb-1.5">建议操作：</p>
                                    <p className="text-xs text-gray-700 leading-relaxed">
                                      请点击上方消息旁的<strong className="text-gray-900 mx-0.5">编辑按钮</strong>，补充更详细的问题描述后重新提交，我将为您提供更准确的分析结果。
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              {/* 提示文字 */}
                              <div className="mt-2 flex items-start gap-1.5">
                                <i className="ri-information-line text-amber-600 text-xs mt-0.5"></i>
                                <p className="text-xs text-amber-800">
                                  问题描述越详细，查询结果越准确
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {message.result && (
                          <>
                            <ProgressPanel />
                            <ResultCard result={message.result} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {isThinking && (
                  <div className="space-y-3">
                    <div className="flex justify-start">
                      <div className="bg-gray-100 px-3 py-2 rounded-2xl rounded-tl-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 停止按钮 */}
                    <div className="flex justify-center">
                      <button
                        onClick={handleStopThinking}
                        className="px-3 py-1.5 flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
                      >
                        <i className="ri-stop-circle-line text-sm"></i>
                        <span>停止</span>
                      </button>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
            {messages.length === 0 && (
              <div className="mb-2">
                <RecommendationCapsule onSelect={(text) => setInputValue(text)} />
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <div className="bg-white rounded-xl border border-gray-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="输入您想了解的问题..."
                    className="w-full px-3 py-2 bg-transparent outline-none resize-none text-xs"
                    rows={2}
                    disabled={isThinking}
                  />
                </div>
                
                {/* 关键词匹配的问题建议 */}
                {matchedQuestions.length > 0 && !isThinking && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg z-10 max-h-48 overflow-y-auto">
                    <div className="p-2">
                      <div className="flex items-center gap-1.5 px-2 py-1 mb-1">
                        <i className="ri-lightbulb-flash-line text-blue-600 text-xs"></i>
                        <span className="text-xs font-medium text-gray-600">相关问题建议</span>
                      </div>
                      <div className="space-y-0.5">
                        {matchedQuestions.map((question, index) => (
                          <button
                            key={index}
                            onClick={() => setInputValue(question)}
                            className="w-full text-left px-2 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-colors cursor-pointer"
                          >
                            <i className="ri-chat-3-line text-gray-400 mr-1.5 text-xs"></i>
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={handleSend}
                disabled={!inputValue.trim() || isThinking}
                className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer h-fit"
              >
                <i className="ri-send-plane-fill text-base"></i>
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 text-center">按 Enter 发送,Shift + Enter 换行</p>
          </div>
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            {/* Modal Header */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-alert-line text-red-600 text-base"></i>
                </div>
                <h3 className="text-sm font-semibold text-gray-900">确认清空聊天记录</h3>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-4 py-4">
              <p className="text-xs text-gray-600 leading-relaxed">
                清空后将删除所有对话历史和查询结果，此操作无法撤销。确定要继续吗？
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
              >
                取消
              </button>
              <button
                onClick={handleClearChat}
                className="px-3 py-1.5 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-xs font-medium"
              >
                <i className="ri-delete-bin-line mr-0.5 text-xs"></i>
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
